import { useCallback, useRef, useState, useMemo, useEffect, useImperativeHandle, Ref, forwardRef } from "react";

import Grid from "@mui/material/Grid";

import { useValidEmailsRef } from "../../../hooks/useValidEmailsRef";
import { EmailRecipientLabel } from "../EmailRecipientLabel";
import { EmailRecipientsChipList } from "../EmailRecipientsChipList/EmailRecipientsChipList";
import { extractEmailsFromString } from "../helpers";
import { CompleteEmailRecipientsInputProps, EmailRecipientsInputRef, EmailsList } from "../types";
import { UnsubscribeEmailWarning } from "../UnsubscribeEmailWarning/UnsubscribeEmailWarning";

import { getUnsubscribedContactEmailsMap, parseEmail } from "./helper";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";

declare global {
  interface Window {
    clipboardData: { getData?: (key: string) => string };
  }
}

export const CompleteEmailRecipientsInput = forwardRef(
  (
    {
      disabled,
      defaultRecipientEmails,
      onChangeRecipients,
      inputOnly,
      inputStyle,
      contactLists,
    }: CompleteEmailRecipientsInputProps,
    ref: Ref<EmailRecipientsInputRef>
  ) => {
    const { t } = useTranslate();
    const { data: unsubscribedContactEmails } = trpc.getUnsubscribedContactEmails.useQuery();
    const unsubscribedContactEmailsMap = useMemo(
      () => getUnsubscribedContactEmailsMap(unsubscribedContactEmails || []),
      [unsubscribedContactEmails]
    );
    const firstRender = useRef(true);
    const defaultEmailList = extractEmailsFromString(defaultRecipientEmails?.join(",") || "");
    const [emailsList, setEmailsList] = useState<EmailsList>(defaultEmailList);

    const allEmails = useMemo(() => {
      const faultyEmails: string[] = [];
      const validEmails: string[] = [];
      const unsubscribeEmails: string[] = [];
      Object.keys(emailsList).forEach((email) => {
        if (unsubscribedContactEmailsMap[email]) {
          unsubscribeEmails.push(email);
        } else if (emailsList[email].valid) {
          validEmails.push(email);
        } else {
          faultyEmails.push(email);
        }
      });
      return { faultyEmails, validEmails, unsubscribeEmails };
    }, [emailsList, unsubscribedContactEmailsMap]);

    const validEmailsRef = useValidEmailsRef(allEmails.validEmails);

    const errorMessage = (() => {
      if (allEmails.faultyEmails.length === 0) {
        return undefined;
      }
      if (Object.keys(emailsList).length === 1) {
        return t("dashboard", "emailDrawer.recipients.input.errorMessage.single");
      } else {
        return t("dashboard", "emailDrawer.recipients.input.errorMessage.multiple");
      }
    })();

    useEffect(() => {
      if (!firstRender.current) {
        const validEmails = Object.keys(emailsList).filter((email) => {
          return Boolean(emailsList[email].valid && !unsubscribedContactEmailsMap[email]);
        });
        // We debounce the saveRecipients call to avoid calling it too often
        const timeout = setTimeout(() => onChangeRecipients?.(validEmails), 500);
        return () => clearTimeout(timeout);
      }

      // This is needed to set the initial value of the input field
      onChangeRecipients?.(allEmails.validEmails);
      firstRender.current = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailsList]);

    const addEmails = useCallback((input: string): void => {
      if (input) {
        const newEmails = parseEmail(input);
        setEmailsList((prevEmailList) => ({ ...prevEmailList, ...newEmails }));
      }
    }, []);

    const updateEmails = useCallback((input: string): void => {
      // Extract emails from the pasted text
      const newEmails = parseEmail(input);
      setEmailsList(newEmails);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        addEmails: async (emails = []) => {
          addEmails(emails.join(","));
        },
        updateEmails: async (emails = []) => {
          updateEmails(emails.join(","));
        },
        getValidEmails: () => {
          return validEmailsRef.current;
        },
      }),
      [addEmails, updateEmails, validEmailsRef]
    );

    const showSelectRecipientsButton =
      !disabled && allEmails.validEmails.length === 0 && !contactLists?.selected.length;
    const displayUnsubscribeWarning = allEmails.unsubscribeEmails && allEmails.unsubscribeEmails.length > 0;

    return (
      <Grid container>
        <EmailRecipientLabel
          disabled={disabled}
          errorMessage={errorMessage}
          hiddenTitle={inputOnly}
          hiddenHelperText={showSelectRecipientsButton}
        >
          <EmailRecipientsChipList
            setEmailsList={setEmailsList}
            emailsList={emailsList}
            parseAndAddEmails={addEmails}
            allEmails={allEmails}
            inputOnly={inputOnly}
            inputStyle={inputStyle}
            disabled={disabled}
            suggestContacts
            contactLists={contactLists}
          />
        </EmailRecipientLabel>

        {displayUnsubscribeWarning && <UnsubscribeEmailWarning unsubscribeEmails={allEmails.unsubscribeEmails} />}
      </Grid>
    );
  }
);

CompleteEmailRecipientsInput.displayName = "CompleteEmailRecipientsInput";
